import axios from 'axios';
import apiClient from './client';
import { apiFailure, EnterpriseApiError } from './errors';
import { isValidTokenDecimals, sanitizeTokenSymbol } from '@/lib/tokenMetadata';
import {
  descriptorRefusalCode,
  parseVaultDescriptorBundle,
  toVaultDescriptorError,
  vaultDescriptorErrorFor,
} from '@/lib/vaultDescriptor';
import type { VaultDescriptorBundle } from '@/lib/vaultDescriptor';
import type { CriticalActionSignature } from '@/types/criticalAction';
import type {
  CreateVaultRequest,
  UpdateVaultRequest,
  SubmitXpubRequest,
  CreateProposalRequest,
  SignProposalRequest,
  RejectProposalRequest,
  UpdatePolicyRequest,
  UpdateWhitelistModeRequest,
  AddWhitelistAddressRequest,
  VaultResponse,
  VaultDetailResponse,
  VaultsListResponse,
  VaultMembersListResponse,
  VaultAddressesListResponse,
  VaultAddressResponse,
  VaultBalancesResponse,
  VaultTransactionsResponse,
  VaultBalanceHistoryResponse,
  OrgPortfolioResponse,
  OrgPortfolioVaultData,
  ProposalResponse,
  FeeEstimateResponse,
  ProposalsListResponse,
  PendingSetupsResponse,
  VaultAuditLogsResponse,
  SimpleVaultResponse,
  PolicyResponse,
  EffectiveLimitsResponse,
  VelocityResponse,
  NetworkFee,
  Vault,
  VaultDetail,
  VaultSearchResult,
  VaultTagDefinition,
  VaultMember,
  VaultAddress,
  VaultBalance,
  VaultTransaction,
  VaultBalanceHistory,
  TxProposal,
  SigningRequest,
  OrgProposalActivity,
  PendingSetup,
  VaultAuditLog,
  VaultToken,
  VaultPolicy,
  VelocityUsage,
  ChainPolicyOverride,
  ProposalRecipient,
  PolicyRule,
  CreatePolicyRuleRequest,
  UpdatePolicyRuleRequest,
  PolicyRulesListResponse,
  PolicyRuleEnvelope,
  PolicyWriteResult,
  PolicyChangeRequest,
  PolicyChangesListResponse,
  PolicyChangeEnvelope,
  PolicyChangeStatus,
  ApprovalGroup,
  CreateApprovalGroupRequest,
  UpdateApprovalGroupRequest,
  ApprovalGroupsListResponse,
  ApprovalGroupEnvelope,
  PolicyPreviewRequest,
  PolicyPreviewResponse,
  PolicyEngineEvaluation,
  PolicyTemplateSummary,
  PolicyTemplatesListResponse,
  ApplyTemplateResponse,
  ApplyTemplateResult,
  Contact,
  CreateContactRequest,
  UpdateContactRequest,
  ContactResponse,
  ContactsListResponse,
  TokenThreatInfo,
  FlaggedAddress,
  SolanaSigningPayload,
  ProposalSimulation,
  SignatureRequest,
  CreateSignatureRequestRequest,
  SignSignatureRequestRequest,
} from '@/types/vault';
import type { ThreatStatus } from '@/types/vault';

interface WatchedTokensResponse {
  success: boolean;
  tokens?: VaultToken[];
  error?: string;
}

interface WatchedTokenResponse {
  success: boolean;
  token?: VaultToken;
  error?: string;
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

export { EnterpriseApiError } from './errors';

/** Build an EnterpriseApiError from a failed proposal-envelope response. */
function proposalError(data: ProposalResponse, fallback: string): EnterpriseApiError {
  return new EnterpriseApiError(
    data.error || fallback,
    typeof data.errorCode === 'string' ? data.errorCode : undefined,
    undefined,
    Array.isArray(data.blockingProposals) ? data.blockingProposals : undefined
  );
}

function buildQueryString(params?: object): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * Interpret a governance-interceptable mutation response. When the backend
 * captured the change (org governance enabled) it returns
 * { governanceRequired:true, changeRequest }; otherwise `value` is the applied
 * entity. Throws (with the server error) only for genuine failures.
 */
function toWriteResult<T>(
  data: {
    success: boolean;
    governanceRequired?: boolean;
    changeRequest?: PolicyChangeRequest;
    error?: string;
  },
  value: T | undefined,
  fallbackError: string
): PolicyWriteResult<T> {
  if (data.governanceRequired && data.changeRequest) {
    return { governanceRequired: true, changeRequest: data.changeRequest };
  }
  if (!data.success) {
    throw apiFailure(data, fallbackError);
  }
  return { governanceRequired: false, value: value as T };
}

export const vaultsApi = {
  // ============================================================
  // Vault CRUD
  // ============================================================

  createVault: async (orgId: string, data: CreateVaultRequest): Promise<Vault> => {
    const response = await apiClient.post<ApiResponse<VaultResponse>>(
      `/organizations/${orgId}/vaults`,
      data
    );
    if (!response.data.data.success || !response.data.data.vault) {
      throw apiFailure(response.data.data, 'Failed to create vault');
    }
    return response.data.data.vault;
  },

  getVaults: async (
    orgId: string,
    params?: { tag?: string; member?: string; sort?: string; order?: string }
  ): Promise<Vault[]> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<VaultsListResponse>>(
      `/organizations/${orgId}/vaults${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get vaults');
    }
    return response.data.data.vaults || [];
  },

  getVaultTags: async (orgId: string): Promise<VaultTagDefinition[]> => {
    const response = await apiClient.get<
      ApiResponse<{ success: boolean; tags?: VaultTagDefinition[]; error?: string }>
    >(`/organizations/${orgId}/vault-tags`);
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get vault tags');
    }
    return response.data.data.tags || [];
  },

  createVaultTag: async (
    orgId: string,
    data: { name: string; description: string; color?: string }
  ): Promise<VaultTagDefinition> => {
    const response = await apiClient.post<
      ApiResponse<{ success: boolean; tag?: VaultTagDefinition; error?: string }>
    >(`/organizations/${orgId}/vault-tags`, data);
    if (!response.data.data.success || !response.data.data.tag) {
      throw apiFailure(response.data.data, 'Failed to create tag');
    }
    return response.data.data.tag;
  },

  updateVaultTag: async (
    orgId: string,
    tagId: string,
    data: { name?: string; description?: string; color?: string }
  ): Promise<VaultTagDefinition> => {
    const response = await apiClient.patch<
      ApiResponse<{ success: boolean; tag?: VaultTagDefinition; error?: string }>
    >(`/organizations/${orgId}/vault-tags/${tagId}`, data);
    if (!response.data.data.success || !response.data.data.tag) {
      throw apiFailure(response.data.data, 'Failed to update tag');
    }
    return response.data.data.tag;
  },

  deleteVaultTag: async (orgId: string, tagId: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<{ success: boolean; error?: string }>>(
      `/organizations/${orgId}/vault-tags/${tagId}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to delete tag');
    }
  },

  searchVaults: async (orgId: string, query: string): Promise<VaultSearchResult[]> => {
    const qs = buildQueryString({ q: query });
    const response = await apiClient.get<
      ApiResponse<{ success: boolean; vaults?: VaultSearchResult[]; error?: string }>
    >(`/organizations/${orgId}/vaults/search${qs}`);
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to search vaults');
    }
    return response.data.data.vaults || [];
  },

  getVault: async (orgId: string, vaultId: string): Promise<VaultDetail> => {
    const response = await apiClient.get<ApiResponse<VaultDetailResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}`
    );
    if (!response.data.data.success || !response.data.data.vault) {
      throw apiFailure(response.data.data, 'Failed to get vault');
    }
    return response.data.data.vault;
  },

  updateVault: async (orgId: string, vaultId: string, data: UpdateVaultRequest): Promise<Vault> => {
    const response = await apiClient.patch<ApiResponse<VaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}`,
      data
    );
    if (!response.data.data.success || !response.data.data.vault) {
      throw apiFailure(response.data.data, 'Failed to update vault');
    }
    return response.data.data.vault;
  },

  archiveVault: async (
    orgId: string,
    vaultId: string,
    signature: CriticalActionSignature
  ): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}`,
      { data: signature }
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to archive vault');
    }
  },

  unarchiveVault: async (
    orgId: string,
    vaultId: string,
    signature: CriticalActionSignature
  ): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/unarchive`,
      signature
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to unarchive vault');
    }
  },

  // ============================================================
  // Vault descriptor (VD-1 / VD-2: device-side pinning)
  // ============================================================

  /**
   * The vault's full signer set + its hash, forwarded unchanged to SSP Wallet /
   * SSP Key on every vault request. 409 (or a "not active" code) means not
   * every signer has submitted their keys yet. The returned objects are the
   * response's own (validated, never copied or normalised).
   */
  getVaultDescriptor: async (orgId: string, vaultId: string): Promise<VaultDescriptorBundle> => {
    let response;
    try {
      response = await apiClient.get<ApiResponse<unknown>>(
        `/organizations/${orgId}/vaults/${vaultId}/descriptor`
      );
    } catch (err) {
      throw toVaultDescriptorError(err);
    }
    const body = response.data as Partial<ApiResponse<unknown>> | undefined;
    const data = body?.data;
    const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
    if (body?.status === 'error' || d.success === false) {
      // Fail closed on every refusal (NOT_MEMBER, MISSING_XPUB, SIGNER_SET_MISMATCH, …).
      throw vaultDescriptorErrorFor(
        descriptorRefusalCode(typeof d.code === 'number' ? d.code : undefined, d.errorCode)
      );
    }
    return parseVaultDescriptorBundle(data, { orgId, vaultId });
  },

  // ============================================================
  // Vault Members
  // ============================================================

  getVaultMembers: async (orgId: string, vaultId: string): Promise<VaultMember[]> => {
    const response = await apiClient.get<ApiResponse<VaultMembersListResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/members`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get vault members');
    }
    return response.data.data.members || [];
  },

  addViewer: async (orgId: string, vaultId: string, wkIdentity: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/members`,
      { wkIdentity }
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to add viewer');
    }
  },

  removeViewer: async (orgId: string, vaultId: string, memberId: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/members/${memberId}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to remove viewer');
    }
  },

  // ============================================================
  // xpub Provisioning
  // ============================================================

  submitXpub: async (orgId: string, vaultId: string, data: SubmitXpubRequest): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/xpub`,
      data
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to submit xpub');
    }
  },

  getPendingSetups: async (): Promise<PendingSetup[]> => {
    const response =
      await apiClient.get<ApiResponse<PendingSetupsResponse>>('/vaults/pending-setup');
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get pending setups');
    }
    return response.data.data.setups || [];
  },

  // ============================================================
  // Addresses
  // ============================================================

  getAddresses: async (orgId: string, vaultId: string): Promise<VaultAddress[]> => {
    const response = await apiClient.get<ApiResponse<VaultAddressesListResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/addresses`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get addresses');
    }
    return response.data.data.addresses || [];
  },

  generateAddress: async (orgId: string, vaultId: string): Promise<VaultAddress> => {
    const response = await apiClient.post<ApiResponse<VaultAddressResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/addresses`
    );
    if (!response.data.data.success || !response.data.data.address) {
      throw apiFailure(response.data.data, 'Failed to generate address');
    }
    return response.data.data.address;
  },

  // Solana enterprise: ensure the multisig + durable nonce account at the
  // given addressIndex exist on-chain. Backed by relay paymaster — idempotent,
  // safe to call before every Solana proposal. Returns the resolved multisig
  // address (and a tx signature if it actually had to provision).
  setupSolana: async (
    orgId: string,
    vaultId: string,
    addressIndex: number
  ): Promise<{
    signature: string | null;
    multisigAddress: string;
    vaultAddress: string;
    nonceAccount: string;
    nonceValue: string;
    alreadyProvisioned: boolean;
  }> => {
    const response = await apiClient.post<
      ApiResponse<{
        success: boolean;
        error?: string;
        errorCode?: string;
        result?: {
          signature: string | null;
          multisigAddress: string;
          vaultAddress: string;
          nonceAccount: string;
          nonceValue: string;
          alreadyProvisioned: boolean;
        };
      }>
    >(`/organizations/${orgId}/vaults/${vaultId}/sol/setup`, { addressIndex });
    if (!response.data.data.success || !response.data.data.result) {
      throw apiFailure(response.data.data, 'Solana setup failed');
    }
    return response.data.data.result;
  },

  // Solana split-flow: fetch this signer's per-signer signing payload (§6.3/§7).
  // Returns the unsigned tx (create or approve kind), signer slot map, and the
  // source addressIndex. The app feeds unsignedTxBase64 to the wallet as
  // rawUnsignedTx and synthesizes inputDetails from addressIndex.
  //
  // Throws an Error with an attached `errorCode` for the documented control
  // flow cases so callers can branch:
  //   409 CREATE_PENDING      — creator tx not yet confirmed; retry shortly
  //   409 ALREADY_SIGNING     — this signer already has an in-flight tx / signature
  //   410 PAYLOAD_SUPERSEDED  — prior payload was superseded; re-fetch + re-sign
  getSolanaSigningPayload: async (
    orgId: string,
    vaultId: string,
    proposalId: string
  ): Promise<SolanaSigningPayload> => {
    const response = await apiClient.post<
      ApiResponse<{
        success?: boolean;
        error?: string;
        errorCode?: string;
        // Success body nests the payload (vaultHandler wraps the service result
        // verbatim via createDataMessage): data.payload.{ ... }. Error body stays
        // flat: data.{ success: false, error, errorCode }.
        payload?: {
          unsignedTxBase64?: string;
          signerSlots?: SolanaSigningPayload['signerSlots'];
          addressIndex?: number;
          kind?: SolanaSigningPayload['kind'];
        };
      }>
    >(`/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/solana-signing-payload`);
    const data = response.data.data;
    const payload = data.payload;
    if (
      !payload ||
      payload.unsignedTxBase64 == null ||
      payload.kind == null ||
      payload.addressIndex == null
    ) {
      const err = new Error(data.error || 'Failed to fetch Solana signing payload');
      if (typeof data.errorCode === 'string') {
        (err as Error & { errorCode?: string }).errorCode = data.errorCode;
      }
      throw err;
    }
    return {
      unsignedTxBase64: payload.unsignedTxBase64,
      signerSlots: payload.signerSlots ?? [],
      addressIndex: payload.addressIndex,
      kind: payload.kind,
    };
  },

  updateAddressLabel: async (
    orgId: string,
    vaultId: string,
    addressIndex: number,
    label: string
  ): Promise<void> => {
    const response = await apiClient.patch<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/addresses/${String(addressIndex)}`,
      { label }
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to update label');
    }
  },

  // ============================================================
  // Balances & Transactions
  // ============================================================

  getBalances: async (
    orgId: string,
    vaultId: string
  ): Promise<{ balances: VaultBalance[]; totalBalance: string; totalBalanceUsd: number }> => {
    const response = await apiClient.get<ApiResponse<VaultBalancesResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/balances`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get balances');
    }
    return {
      balances: response.data.data.balances || [],
      totalBalance: response.data.data.totalBalance || '0',
      totalBalanceUsd: response.data.data.totalBalanceUsd || 0,
    };
  },

  getTransactions: async (
    orgId: string,
    vaultId: string,
    params?: { limit?: number; skip?: number; address?: string; direction?: string }
  ): Promise<{ transactions: VaultTransaction[]; total: number }> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<VaultTransactionsResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/transactions${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get transactions');
    }
    return {
      transactions: response.data.data.transactions || [],
      total: response.data.data.total || 0,
    };
  },

  syncVault: async (orgId: string, vaultId: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/sync`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to sync vault');
    }
  },

  getBalanceHistory: async (
    orgId: string,
    vaultId: string,
    params?: { limit?: number }
  ): Promise<VaultBalanceHistory[]> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<VaultBalanceHistoryResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/balance-history${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get balance history');
    }
    return response.data.data.history || [];
  },

  /**
   * Aggregated portfolio for all active vaults in one request — balances,
   * recent transactions, pending proposals, and compact balance history per
   * vault. Replaces the portfolio page's per-vault 4-request fan-out.
   */
  getOrgPortfolio: async (
    orgId: string,
    params?: { historyLimit?: number; txLimit?: number }
  ): Promise<OrgPortfolioVaultData[]> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<OrgPortfolioResponse>>(
      `/organizations/${orgId}/portfolio${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get portfolio');
    }
    return response.data.data.vaults || [];
  },

  // ============================================================
  // Proposals
  // ============================================================

  createProposal: async (
    orgId: string,
    vaultId: string,
    data: CreateProposalRequest
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals`,
      data
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      // errorCode + blockingProposals (FUNDS_RESERVED / VAULT_BUSY /
      // VAULT_DEPLOYMENT_PENDING) reach the caller on the thrown error.
      throw proposalError(response.data.data, 'Failed to create proposal');
    }
    return response.data.data.proposal;
  },

  /**
   * Add payments to a pending UTXO native proposal. The transaction is
   * rebuilt, so every signature and approval is cleared and signers sign
   * again. Amounts are smallest-unit integer strings. Failures throw an
   * EnterpriseApiError (FUNDS_RESERVED carries blockingProposals).
   */
  amendProposal: async (
    orgId: string,
    vaultId: string,
    proposalId: string,
    addRecipients: ProposalRecipient[],
    feePerByte?: number
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/amend`,
      {
        addRecipients: addRecipients.map((r) => ({ address: r.address, amount: r.amount })),
        ...(feePerByte != null ? { feePerByte } : {}),
      }
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      throw proposalError(response.data.data, 'Failed to add the payment');
    }
    return response.data.data.proposal;
  },

  /**
   * Fee estimate for a proposal-to-be. Never throws on a business failure:
   * `{ success: false, errorCode, blockingProposals? }` is returned as data
   * (FUNDS_RESERVED / VAULT_BUSY / VAULT_DEPLOYMENT_PENDING arrive here early).
   */
  estimateProposalFee: async (
    orgId: string,
    vaultId: string,
    data: CreateProposalRequest
  ): Promise<FeeEstimateResponse> => {
    const response = await apiClient.post<ApiResponse<FeeEstimateResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/estimate-fee`,
      data
    );
    return response.data.data;
  },

  getProposals: async (
    orgId: string,
    vaultId: string,
    params?: { status?: string; limit?: number; skip?: number }
  ): Promise<{ proposals: TxProposal[]; total: number }> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<ProposalsListResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get proposals');
    }
    return {
      proposals: response.data.data.proposals || [],
      total: response.data.data.total || 0,
    };
  },

  getProposal: async (orgId: string, vaultId: string, proposalId: string): Promise<TxProposal> => {
    const response = await apiClient.get<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}`
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      throw apiFailure(response.data.data, 'Failed to get proposal');
    }
    return response.data.data.proposal;
  },

  signProposal: async (
    orgId: string,
    vaultId: string,
    proposalId: string,
    data: SignProposalRequest
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/sign`,
      data
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      const err = new Error(response.data.data.error || 'Failed to sign proposal');
      // Forward errorCode so callers can branch on documented split-flow cases
      // (the canonical PAYLOAD_SUPERSEDED emitter is this sign POST — §6.3.3).
      const respData = response.data.data as unknown as Record<string, unknown>;
      if (typeof respData.errorCode === 'string') {
        (err as Error & { errorCode?: string }).errorCode = respData.errorCode;
      }
      throw err;
    }
    return response.data.data.proposal;
  },

  rejectProposal: async (
    orgId: string,
    vaultId: string,
    proposalId: string,
    data: RejectProposalRequest
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/reject`,
      data
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      throw apiFailure(response.data.data, 'Failed to reject proposal');
    }
    return response.data.data.proposal;
  },

  cancelProposal: async (
    orgId: string,
    vaultId: string,
    proposalId: string
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/cancel`
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      throw proposalError(response.data.data, 'Failed to cancel proposal');
    }
    return response.data.data.proposal;
  },

  retryBroadcast: async (
    orgId: string,
    vaultId: string,
    proposalId: string
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/retry-broadcast`
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      // PROPOSAL_STALE: the proposal can never land — rebuild it instead.
      throw proposalError(response.data.data, 'Failed to retry broadcast');
    }
    return response.data.data.proposal;
  },

  // ============================================================
  // Signature Requests (WalletConnect Phase 2 — vault message signing)
  // ============================================================

  createSignatureRequest: async (
    orgId: string,
    vaultId: string,
    data: CreateSignatureRequestRequest
  ): Promise<SignatureRequest> => {
    const response = await apiClient.post<
      ApiResponse<{
        success: boolean;
        request?: SignatureRequest;
        error?: string;
        errorCode?: string;
      }>
    >(`/organizations/${orgId}/vaults/${vaultId}/signature-requests`, data);
    if (!response.data.data.success || !response.data.data.request) {
      const err = new Error(response.data.data.error || 'Failed to create signature request');
      const respData = response.data.data as unknown as Record<string, unknown>;
      if (typeof respData.errorCode === 'string') {
        (err as Error & { errorCode?: string }).errorCode = respData.errorCode;
      }
      throw err;
    }
    return response.data.data.request;
  },

  getSignatureRequests: async (
    orgId: string,
    vaultId: string,
    params?: { status?: string; limit?: number }
  ): Promise<SignatureRequest[]> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<
      ApiResponse<{ success: boolean; requests?: SignatureRequest[]; error?: string }>
    >(`/organizations/${orgId}/vaults/${vaultId}/signature-requests${qs}`);
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get signature requests');
    }
    return response.data.data.requests || [];
  },

  getSignatureDeploymentStatus: async (
    orgId: string,
    vaultId: string,
    addressIndex?: number
  ): Promise<{ deployed?: boolean; address?: string }> => {
    const qs = buildQueryString(addressIndex != null ? { addressIndex } : undefined);
    const response = await apiClient.get<
      ApiResponse<{ success: boolean; deployed?: boolean; address?: string }>
    >(`/organizations/${orgId}/vaults/${vaultId}/signature-requests/deployment-status${qs}`);
    return {
      deployed: response.data.data.deployed,
      address: response.data.data.address,
    };
  },

  getSignatureRequest: async (
    orgId: string,
    vaultId: string,
    requestId: string
  ): Promise<SignatureRequest> => {
    const response = await apiClient.get<
      ApiResponse<{ success: boolean; request?: SignatureRequest; error?: string }>
    >(`/organizations/${orgId}/vaults/${vaultId}/signature-requests/${requestId}`);
    if (!response.data.data.success || !response.data.data.request) {
      throw apiFailure(response.data.data, 'Failed to get signature request');
    }
    return response.data.data.request;
  },

  signSignatureRequest: async (
    orgId: string,
    vaultId: string,
    requestId: string,
    data: SignSignatureRequestRequest
  ): Promise<SignatureRequest> => {
    const response = await apiClient.post<
      ApiResponse<{
        success: boolean;
        request?: SignatureRequest;
        error?: string;
        errorCode?: string;
      }>
    >(`/organizations/${orgId}/vaults/${vaultId}/signature-requests/${requestId}/sign`, data);
    if (!response.data.data.success || !response.data.data.request) {
      const err = new Error(response.data.data.error || 'Failed to sign signature request');
      const respData = response.data.data as unknown as Record<string, unknown>;
      if (typeof respData.errorCode === 'string') {
        (err as Error & { errorCode?: string }).errorCode = respData.errorCode;
      }
      throw err;
    }
    return response.data.data.request;
  },

  rejectSignatureRequest: async (
    orgId: string,
    vaultId: string,
    requestId: string,
    reason?: string
  ): Promise<SignatureRequest> => {
    const response = await apiClient.post<
      ApiResponse<{ success: boolean; request?: SignatureRequest; error?: string }>
    >(`/organizations/${orgId}/vaults/${vaultId}/signature-requests/${requestId}/reject`, {
      reason,
    });
    if (!response.data.data.success || !response.data.data.request) {
      throw apiFailure(response.data.data, 'Failed to reject signature request');
    }
    return response.data.data.request;
  },

  // ============================================================
  // Audit Log
  // ============================================================

  getAuditLog: async (
    orgId: string,
    vaultId: string,
    params?: { limit?: number; skip?: number; eventType?: string }
  ): Promise<{ logs: VaultAuditLog[]; total: number }> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<VaultAuditLogsResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/audit${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get audit log');
    }
    return {
      logs: response.data.data.logs || [],
      total: response.data.data.total || 0,
    };
  },

  getOrgVaultAuditLog: async (
    orgId: string,
    params?: { limit?: number; skip?: number; eventType?: string; vaultId?: string }
  ): Promise<{ logs: VaultAuditLog[]; total: number }> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<VaultAuditLogsResponse>>(
      `/organizations/${orgId}/vault-audit${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get vault audit log');
    }
    return {
      logs: response.data.data.logs || [],
      total: response.data.data.total || 0,
    };
  },

  // ============================================================
  // Watched Tokens (EVM vaults)
  // ============================================================

  getWatchedTokens: async (orgId: string, vaultId: string): Promise<VaultToken[]> => {
    const response = await apiClient.get<ApiResponse<WatchedTokensResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/tokens`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get watched tokens');
    }
    return response.data.data.tokens || [];
  },

  addWatchedToken: async (
    orgId: string,
    vaultId: string,
    data: { contract: string; name: string; symbol: string; decimals: number; logo?: string }
  ): Promise<VaultToken> => {
    const response = await apiClient.post<ApiResponse<WatchedTokenResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/tokens`,
      data
    );
    if (!response.data.data.success || !response.data.data.token) {
      throw apiFailure(response.data.data, 'Failed to add token');
    }
    return response.data.data.token;
  },

  removeWatchedToken: async (orgId: string, vaultId: string, contract: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/tokens/${encodeURIComponent(contract)}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to remove token');
    }
  },

  // ============================================================
  // Token Threat Detection
  // ============================================================

  getOrgTokenThreats: async (orgId: string): Promise<TokenThreatInfo[]> => {
    const response = await apiClient.get<
      ApiResponse<{ success: boolean; threats?: TokenThreatInfo[]; error?: string }>
    >(`/organizations/${orgId}/token-threats`);
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get token threats');
    }
    return response.data.data.threats || [];
  },

  getTokenThreats: async (orgId: string, vaultId: string): Promise<TokenThreatInfo[]> => {
    const response = await apiClient.get<
      ApiResponse<{ success: boolean; threats?: TokenThreatInfo[]; error?: string }>
    >(`/organizations/${orgId}/vaults/${vaultId}/token-threats`);
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get token threats');
    }
    return response.data.data.threats || [];
  },

  setThreatOverride: async (
    orgId: string,
    data: { chain: string; contract: string; status: ThreatStatus; reason?: string }
  ): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/token-threats/override`,
      data
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to set threat override');
    }
  },

  removeThreatOverride: async (orgId: string, chain: string, contract: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/token-threats/override/${encodeURIComponent(chain)}/${encodeURIComponent(contract)}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to remove threat override');
    }
  },

  // ============================================================
  // Transaction Spam Flagging
  // ============================================================

  flagTransactionSpam: async (orgId: string, vaultId: string, txId: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/transactions/${txId}/spam-flag`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to flag transaction');
    }
  },

  unflagTransactionSpam: async (orgId: string, vaultId: string, txId: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/transactions/${txId}/spam-flag`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to unflag transaction');
    }
  },

  getFlaggedTransactions: async (orgId: string): Promise<VaultTransaction[]> => {
    const response = await apiClient.get<
      ApiResponse<{ success: boolean; transactions?: VaultTransaction[]; error?: string }>
    >(`/organizations/${orgId}/flagged-transactions`);
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get flagged transactions');
    }
    return response.data.data.transactions || [];
  },

  // ============================================================
  // Address Flagging
  // ============================================================

  flagAddress: async (
    orgId: string,
    data: { address: string; chain?: string; reason?: string }
  ): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/flagged-addresses`,
      data
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to flag address');
    }
  },

  unflagAddress: async (orgId: string, address: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/flagged-addresses/${encodeURIComponent(address)}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to unflag address');
    }
  },

  getFlaggedAddresses: async (orgId: string): Promise<FlaggedAddress[]> => {
    const response = await apiClient.get<
      ApiResponse<{ success: boolean; addresses?: FlaggedAddress[]; error?: string }>
    >(`/organizations/${orgId}/flagged-addresses`);
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get flagged addresses');
    }
    return response.data.data.addresses || [];
  },

  fetchKnownTokens: async (
    network: string
  ): Promise<Array<{ contract: string; name: string; symbol: string; decimals: number }>> => {
    const baseUrl = import.meta.env.VITE_API_URL || 'https://relay.sspwallet.com';
    const response = await axios.get<
      Array<{ contract: string; name: string; symbol: string; decimals: number }>
    >(`${baseUrl}/v1/known-tokens/${encodeURIComponent(network)}`);
    // Drop entries whose decimals can't be trusted; display-safe symbols (ENTX-A-2).
    const tokens = Array.isArray(response.data) ? response.data : [];
    return tokens.flatMap((tok) => {
      const symbol = sanitizeTokenSymbol(tok.symbol);
      return typeof tok.contract === 'string' && symbol && isValidTokenDecimals(tok.decimals)
        ? [{ ...tok, symbol }]
        : [];
    });
  },

  fetchTokenInfo: async (network: string, contract: string): Promise<VaultToken> => {
    const baseUrl = import.meta.env.VITE_API_URL || 'https://relay.sspwallet.com';
    const response = await axios.get<{
      name?: string | null;
      symbol?: string | null;
      decimals?: number | null;
      logo?: string | null;
    }>(`${baseUrl}/v1/tokeninfo/${encodeURIComponent(network)}/${encodeURIComponent(contract)}`);
    const symbol = sanitizeTokenSymbol(response.data.symbol);
    if (!response.data.name || !symbol || !isValidTokenDecimals(response.data.decimals)) {
      throw new Error('Token metadata unavailable — contract may not be a valid ERC-20 token');
    }
    return {
      contract,
      name: response.data.name,
      symbol,
      decimals: response.data.decimals,
      logo: response.data.logo || undefined,
    };
  },

  // ============================================================
  // Vault Freeze / Unfreeze (Critical Actions)
  // ============================================================

  freezeVault: async (
    orgId: string,
    vaultId: string,
    signature: CriticalActionSignature,
    reason?: string
  ): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/freeze`,
      { ...signature, reason }
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to freeze vault');
    }
  },

  unfreezeVault: async (
    orgId: string,
    vaultId: string,
    signature: CriticalActionSignature
  ): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/unfreeze`,
      signature
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to unfreeze vault');
    }
  },

  // ============================================================
  // Vault Member Role Management (Critical Actions)
  // ============================================================

  promoteToAdmin: async (
    orgId: string,
    vaultId: string,
    wkIdentity: string,
    signature: CriticalActionSignature
  ): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/members/${encodeURIComponent(wkIdentity)}/promote`,
      signature
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to promote member');
    }
  },

  demoteFromAdmin: async (
    orgId: string,
    vaultId: string,
    wkIdentity: string,
    signature: CriticalActionSignature
  ): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/members/${encodeURIComponent(wkIdentity)}/demote`,
      signature
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to demote member');
    }
  },

  // ============================================================
  // Spending Policies
  // ============================================================

  getPolicy: async (orgId: string, vaultId: string): Promise<VaultPolicy> => {
    const response = await apiClient.get<ApiResponse<PolicyResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy`
    );
    if (!response.data.data.success || !response.data.data.policy) {
      throw apiFailure(response.data.data, 'Failed to get policy');
    }
    return response.data.data.policy;
  },

  updatePolicy: async (
    orgId: string,
    vaultId: string,
    data: UpdatePolicyRequest
  ): Promise<PolicyWriteResult<VaultPolicy>> => {
    const response = await apiClient.put<ApiResponse<PolicyResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy`,
      data
    );
    const d = response.data.data;
    return toWriteResult(d, d.policy, 'Failed to update policy');
  },

  updateWhitelistMode: async (
    orgId: string,
    vaultId: string,
    data: UpdateWhitelistModeRequest
  ): Promise<PolicyWriteResult<VaultPolicy>> => {
    const response = await apiClient.put<ApiResponse<PolicyResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy/whitelist-mode`,
      data
    );
    const d = response.data.data;
    return toWriteResult(d, d.policy, 'Failed to update whitelist mode');
  },

  addWhitelistAddress: async (
    orgId: string,
    vaultId: string,
    data: AddWhitelistAddressRequest
  ): Promise<PolicyWriteResult<VaultPolicy>> => {
    const response = await apiClient.post<ApiResponse<PolicyResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy/whitelist`,
      data
    );
    const d = response.data.data;
    return toWriteResult(d, d.policy, 'Failed to add whitelist address');
  },

  removeWhitelistAddress: async (
    orgId: string,
    vaultId: string,
    address: string
  ): Promise<PolicyWriteResult<VaultPolicy>> => {
    const response = await apiClient.delete<ApiResponse<PolicyResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy/whitelist/${encodeURIComponent(address)}`
    );
    const d = response.data.data;
    return toWriteResult(d, d.policy, 'Failed to remove whitelist address');
  },

  getVelocity: async (orgId: string, vaultId: string, signer?: string): Promise<VelocityUsage> => {
    const qs = signer ? `?signer=${encodeURIComponent(signer)}` : '';
    const response = await apiClient.get<ApiResponse<VelocityResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy/velocity${qs}`
    );
    if (!response.data.data.success || !response.data.data.velocity) {
      throw apiFailure(response.data.data, 'Failed to get velocity');
    }
    return response.data.data.velocity;
  },

  getOrgPolicy: async (orgId: string): Promise<VaultPolicy> => {
    const response = await apiClient.get<ApiResponse<PolicyResponse>>(
      `/organizations/${orgId}/policy`
    );
    if (!response.data.data.success || !response.data.data.policy) {
      throw apiFailure(response.data.data, 'Failed to get org policy');
    }
    return response.data.data.policy;
  },

  updateOrgPolicy: async (
    orgId: string,
    data: UpdatePolicyRequest
  ): Promise<PolicyWriteResult<VaultPolicy>> => {
    const response = await apiClient.put<ApiResponse<PolicyResponse>>(
      `/organizations/${orgId}/policy`,
      data
    );
    const d = response.data.data;
    return toWriteResult(d, d.policy, 'Failed to update org policy');
  },

  getOrgChainPolicy: async (orgId: string, chain: string): Promise<VaultPolicy> => {
    const response = await apiClient.get<ApiResponse<PolicyResponse>>(
      `/organizations/${orgId}/policy/chain/${chain}`
    );
    if (!response.data.data.success || !response.data.data.policy) {
      throw apiFailure(response.data.data, 'Failed to get chain policy');
    }
    return response.data.data.policy;
  },

  updateOrgChainPolicy: async (
    orgId: string,
    chain: string,
    data: ChainPolicyOverride
  ): Promise<PolicyWriteResult<VaultPolicy>> => {
    const response = await apiClient.put<ApiResponse<PolicyResponse>>(
      `/organizations/${orgId}/policy/chain/${chain}`,
      data
    );
    const d = response.data.data;
    return toWriteResult(d, d.policy, 'Failed to update chain policy');
  },

  deleteOrgChainPolicy: async (orgId: string, chain: string): Promise<PolicyWriteResult<void>> => {
    const response = await apiClient.delete<ApiResponse<PolicyResponse>>(
      `/organizations/${orgId}/policy/chain/${chain}`
    );
    const d = response.data.data;
    return toWriteResult(d, undefined, 'Failed to delete chain policy');
  },

  // ============================================================
  // Policy Rules (ordered rules engine)
  // ============================================================

  getPolicyRules: async (orgId: string, vaultId: string): Promise<PolicyRule[]> => {
    const response = await apiClient.get<ApiResponse<PolicyRulesListResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy-rules`
    );
    if (!response.data.data.success || !response.data.data.rules) {
      throw apiFailure(response.data.data, 'Failed to get policy rules');
    }
    return response.data.data.rules;
  },

  createPolicyRule: async (
    orgId: string,
    vaultId: string,
    data: CreatePolicyRuleRequest
  ): Promise<PolicyWriteResult<PolicyRule>> => {
    const response = await apiClient.post<ApiResponse<PolicyRuleEnvelope>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy-rules`,
      data
    );
    const d = response.data.data;
    return toWriteResult(d, d.rule, 'Failed to create policy rule');
  },

  updatePolicyRule: async (
    orgId: string,
    vaultId: string,
    ruleId: string,
    data: UpdatePolicyRuleRequest
  ): Promise<PolicyWriteResult<PolicyRule>> => {
    const response = await apiClient.put<ApiResponse<PolicyRuleEnvelope>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy-rules/${encodeURIComponent(ruleId)}`,
      data
    );
    const d = response.data.data;
    return toWriteResult(d, d.rule, 'Failed to update policy rule');
  },

  deletePolicyRule: async (
    orgId: string,
    vaultId: string,
    ruleId: string
  ): Promise<PolicyWriteResult<void>> => {
    const response = await apiClient.delete<ApiResponse<PolicyRuleEnvelope>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy-rules/${encodeURIComponent(ruleId)}`
    );
    const d = response.data.data;
    return toWriteResult(d, undefined, 'Failed to delete policy rule');
  },

  reorderPolicyRules: async (
    orgId: string,
    vaultId: string,
    ruleIds: string[]
  ): Promise<PolicyWriteResult<PolicyRule[]>> => {
    const response = await apiClient.put<ApiResponse<PolicyRulesListResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/policy-rules/reorder`,
      { ruleIds }
    );
    const d = response.data.data;
    return toWriteResult(d, d.rules, 'Failed to reorder policy rules');
  },

  // ============================================================
  // Policy Templates (Phase 5)
  // ============================================================

  /** List the code-resident policy templates (no org scope, session-auth only). */
  getPolicyTemplates: async (): Promise<PolicyTemplateSummary[]> => {
    const response =
      await apiClient.get<ApiResponse<PolicyTemplatesListResponse>>('/policy-templates');
    if (!response.data.data.success || !response.data.data.templates) {
      throw new EnterpriseApiError(
        response.data.data.error || 'Failed to load policy templates',
        response.data.data.errorCode
      );
    }
    return response.data.data.templates;
  },

  /**
   * Dry-run a template against a vault (preview:true — never persists, never
   * runs governance). Returns the exact rules + flat fields it would apply.
   */
  previewTemplate: async (
    orgId: string,
    vaultId: string,
    templateId: string
  ): Promise<PolicyTemplateSummary> => {
    const response = await apiClient.post<ApiResponse<ApplyTemplateResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/apply-template`,
      { templateId, preview: true }
    );
    const d = response.data.data;
    if (!d.success || !d.template) {
      throw new EnterpriseApiError(d.error || 'Failed to preview template', d.errorCode);
    }
    return d.template;
  },

  /**
   * Apply a template. Governance-aware: when org governance intercepts, the
   * result carries a change request instead of applied rules (nothing applied
   * yet — approving the request replays the apply).
   */
  applyTemplate: async (
    orgId: string,
    vaultId: string,
    templateId: string
  ): Promise<ApplyTemplateResult> => {
    const response = await apiClient.post<ApiResponse<ApplyTemplateResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/apply-template`,
      { templateId }
    );
    const d = response.data.data;
    if (!d.success) {
      throw new EnterpriseApiError(d.error || 'Failed to apply template', d.errorCode);
    }
    if (d.governanceRequired && d.changeRequest) {
      return { governanceRequired: true, changeRequest: d.changeRequest };
    }
    return {
      governanceRequired: false,
      templateId: d.templateId ?? templateId,
      appliedRules: d.appliedRules ?? [],
      flatFieldsApplied: d.flatFieldsApplied ?? [],
    };
  },

  // ============================================================
  // Org-level Policy Rules (Phase 4 — vaultId absent, separate priority space)
  // ============================================================

  getOrgPolicyRules: async (orgId: string): Promise<PolicyRule[]> => {
    const response = await apiClient.get<ApiResponse<PolicyRulesListResponse>>(
      `/organizations/${orgId}/policy-rules`
    );
    if (!response.data.data.success || !response.data.data.rules) {
      throw apiFailure(response.data.data, 'Failed to get org policy rules');
    }
    return response.data.data.rules;
  },

  createOrgPolicyRule: async (
    orgId: string,
    data: CreatePolicyRuleRequest
  ): Promise<PolicyWriteResult<PolicyRule>> => {
    const response = await apiClient.post<ApiResponse<PolicyRuleEnvelope>>(
      `/organizations/${orgId}/policy-rules`,
      data
    );
    const d = response.data.data;
    return toWriteResult(d, d.rule, 'Failed to create org policy rule');
  },

  updateOrgPolicyRule: async (
    orgId: string,
    ruleId: string,
    data: UpdatePolicyRuleRequest
  ): Promise<PolicyWriteResult<PolicyRule>> => {
    const response = await apiClient.put<ApiResponse<PolicyRuleEnvelope>>(
      `/organizations/${orgId}/policy-rules/${encodeURIComponent(ruleId)}`,
      data
    );
    const d = response.data.data;
    return toWriteResult(d, d.rule, 'Failed to update org policy rule');
  },

  deleteOrgPolicyRule: async (orgId: string, ruleId: string): Promise<PolicyWriteResult<void>> => {
    const response = await apiClient.delete<ApiResponse<PolicyRuleEnvelope>>(
      `/organizations/${orgId}/policy-rules/${encodeURIComponent(ruleId)}`
    );
    const d = response.data.data;
    return toWriteResult(d, undefined, 'Failed to delete org policy rule');
  },

  reorderOrgPolicyRules: async (
    orgId: string,
    ruleIds: string[]
  ): Promise<PolicyWriteResult<PolicyRule[]>> => {
    const response = await apiClient.put<ApiResponse<PolicyRulesListResponse>>(
      `/organizations/${orgId}/policy-rules/reorder`,
      { ruleIds }
    );
    const d = response.data.data;
    return toWriteResult(d, d.rules, 'Failed to reorder org policy rules');
  },

  // ============================================================
  // Policy Change Governance (Phase 4)
  // ============================================================

  getPolicyChanges: async (
    orgId: string,
    status?: PolicyChangeStatus
  ): Promise<PolicyChangeRequest[]> => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await apiClient.get<ApiResponse<PolicyChangesListResponse>>(
      `/organizations/${orgId}/policy-changes${qs}`
    );
    if (!response.data.data.success || !response.data.data.changeRequests) {
      throw new EnterpriseApiError(
        response.data.data.error || 'Failed to load policy changes',
        response.data.data.errorCode
      );
    }
    return response.data.data.changeRequests;
  },

  approvePolicyChange: async (orgId: string, requestId: string): Promise<PolicyChangeRequest> => {
    const response = await apiClient.post<ApiResponse<PolicyChangeEnvelope>>(
      `/organizations/${orgId}/policy-changes/${encodeURIComponent(requestId)}/approve`
    );
    if (!response.data.data.success || !response.data.data.changeRequest) {
      throw new EnterpriseApiError(
        response.data.data.error || 'Failed to approve change',
        response.data.data.errorCode
      );
    }
    return response.data.data.changeRequest;
  },

  rejectPolicyChange: async (orgId: string, requestId: string): Promise<PolicyChangeRequest> => {
    const response = await apiClient.post<ApiResponse<PolicyChangeEnvelope>>(
      `/organizations/${orgId}/policy-changes/${encodeURIComponent(requestId)}/reject`
    );
    if (!response.data.data.success || !response.data.data.changeRequest) {
      throw new EnterpriseApiError(
        response.data.data.error || 'Failed to reject change',
        response.data.data.errorCode
      );
    }
    return response.data.data.changeRequest;
  },

  // ============================================================
  // Approval Groups (org-scoped, Phase 2)
  // ============================================================

  getApprovalGroups: async (orgId: string): Promise<ApprovalGroup[]> => {
    const response = await apiClient.get<ApiResponse<ApprovalGroupsListResponse>>(
      `/organizations/${orgId}/approval-groups`
    );
    if (!response.data.data.success || !response.data.data.groups) {
      throw new EnterpriseApiError(
        response.data.data.error || 'Failed to get approval groups',
        response.data.data.errorCode
      );
    }
    return response.data.data.groups;
  },

  createApprovalGroup: async (
    orgId: string,
    data: CreateApprovalGroupRequest
  ): Promise<ApprovalGroup> => {
    const response = await apiClient.post<ApiResponse<ApprovalGroupEnvelope>>(
      `/organizations/${orgId}/approval-groups`,
      data
    );
    if (!response.data.data.success || !response.data.data.group) {
      throw new EnterpriseApiError(
        response.data.data.error || 'Failed to create approval group',
        response.data.data.errorCode
      );
    }
    return response.data.data.group;
  },

  updateApprovalGroup: async (
    orgId: string,
    groupId: string,
    data: UpdateApprovalGroupRequest
  ): Promise<ApprovalGroup> => {
    const response = await apiClient.put<ApiResponse<ApprovalGroupEnvelope>>(
      `/organizations/${orgId}/approval-groups/${encodeURIComponent(groupId)}`,
      data
    );
    if (!response.data.data.success || !response.data.data.group) {
      throw new EnterpriseApiError(
        response.data.data.error || 'Failed to update approval group',
        response.data.data.errorCode
      );
    }
    return response.data.data.group;
  },

  // Deliberately available after a plan downgrade (cleanup is never gated).
  // GROUP_IN_USE failures carry the referencing rule names on the error.
  deleteApprovalGroup: async (orgId: string, groupId: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<ApprovalGroupEnvelope>>(
      `/organizations/${orgId}/approval-groups/${encodeURIComponent(groupId)}`
    );
    if (!response.data.data.success) {
      throw new EnterpriseApiError(
        response.data.data.error || 'Failed to delete approval group',
        response.data.data.errorCode,
        response.data.data.referencingRules
      );
    }
  },

  // ============================================================
  // Multi-Round Workflow Actions (Phase 2)
  // ============================================================

  // Any resolved approver of the CURRENT round may act — authorization is
  // enforced inside the service (fail-closed), not by vault-admin role.
  workflowApproveProposal: async (
    orgId: string,
    vaultId: string,
    proposalId: string
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/workflow-approve`
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      throw new EnterpriseApiError(
        response.data.data.error || 'Failed to approve proposal',
        response.data.data.errorCode
      );
    }
    return response.data.data.proposal;
  },

  workflowRejectProposal: async (
    orgId: string,
    vaultId: string,
    proposalId: string,
    reason?: string
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/workflow-reject`,
      { reason }
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      throw new EnterpriseApiError(
        response.data.data.error || 'Failed to reject proposal',
        response.data.data.errorCode
      );
    }
    return response.data.data.proposal;
  },

  // ============================================================
  // Admin Approval & Time-Lock
  // ============================================================

  adminApproveProposal: async (
    orgId: string,
    vaultId: string,
    proposalId: string
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/admin-approve`
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      throw apiFailure(response.data.data, 'Failed to approve proposal');
    }
    return response.data.data.proposal;
  },

  adminRejectProposal: async (
    orgId: string,
    vaultId: string,
    proposalId: string,
    reason?: string
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/admin-reject`,
      { reason }
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      throw apiFailure(response.data.data, 'Failed to reject proposal');
    }
    return response.data.data.proposal;
  },

  cancelTimeLock: async (
    orgId: string,
    vaultId: string,
    proposalId: string
  ): Promise<TxProposal> => {
    const response = await apiClient.post<ApiResponse<ProposalResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/cancel-timelock`
    );
    if (!response.data.data.success || !response.data.data.proposal) {
      throw apiFailure(response.data.data, 'Failed to cancel time-lock');
    }
    return response.data.data.proposal;
  },

  // ============================================================
  // Policy Preview
  // ============================================================

  previewPolicy: async (
    orgId: string,
    vaultId: string,
    data: PolicyPreviewRequest
  ): Promise<PolicyEngineEvaluation | null> => {
    try {
      const response = await apiClient.post<ApiResponse<PolicyPreviewResponse>>(
        `/organizations/${orgId}/vaults/${vaultId}/proposals/preview-policy`,
        data
      );
      if (!response.data.data.success || !response.data.data.evaluation) {
        return null;
      }
      return response.data.data.evaluation;
    } catch {
      return null; // Non-blocking — policy preview failure doesn't prevent proposal
    }
  },

  // ============================================================
  // Transaction Simulation (advisory, never gates signing)
  // ============================================================

  // Re-simulate an existing proposal on demand (signer/admin only, rate-limited).
  // Returns the updated simulation, or null on failure (never throws — advisory).
  simulateProposal: async (
    orgId: string,
    vaultId: string,
    proposalId: string
  ): Promise<ProposalSimulation | null> => {
    try {
      const response = await apiClient.post<
        ApiResponse<{
          success: boolean;
          simulation?: ProposalSimulation;
          error?: string;
        }>
      >(`/organizations/${orgId}/vaults/${vaultId}/proposals/${proposalId}/simulate`);
      if (!response.data.data.success || !response.data.data.simulation) {
        return null;
      }
      return response.data.data.simulation;
    } catch {
      return null; // Non-blocking — simulation failure must not strand signing
    }
  },

  // Pre-create dry-run from a CreateProposalRequest-shaped body so NewProposalPage
  // can warn before the proposal exists. Returns null on failure (advisory).
  previewSimulate: async (
    orgId: string,
    vaultId: string,
    body: {
      chain: string;
      recipients: ProposalRecipient[];
      fee?: string;
      tokenContract?: string;
      tokenSymbol?: string;
      tokenDecimals?: number;
      customData?: string;
      evmUserOp?: Record<string, string>;
    }
  ): Promise<ProposalSimulation | null> => {
    try {
      const response = await apiClient.post<
        ApiResponse<{
          success: boolean;
          simulation?: ProposalSimulation;
          error?: string;
        }>
      >(`/organizations/${orgId}/vaults/${vaultId}/proposals/preview-simulate`, body);
      if (!response.data.data.success || !response.data.data.simulation) {
        return null;
      }
      return response.data.data.simulation;
    } catch {
      return null; // Non-blocking — preview failure doesn't prevent proposal
    }
  },

  // ============================================================
  // Contacts
  // ============================================================

  getContacts: async (
    orgId: string,
    params?: { chain?: string; search?: string; limit?: number; skip?: number }
  ): Promise<{ contacts: Contact[]; total: number }> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<ContactsListResponse>>(
      `/organizations/${orgId}/contacts${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get contacts');
    }
    return {
      contacts: response.data.data.contacts || [],
      total: response.data.data.total || 0,
    };
  },

  /** Every contact in the org — pages past the server's 200-per-request cap. */
  getAllContacts: async (orgId: string): Promise<Contact[]> => {
    const all: Contact[] = [];
    for (;;) {
      const { contacts, total } = await vaultsApi.getContacts(orgId, {
        limit: 200,
        skip: all.length,
      });
      all.push(...contacts);
      if (contacts.length === 0 || all.length >= total) return all;
    }
  },

  createContact: async (orgId: string, data: CreateContactRequest): Promise<Contact> => {
    const response = await apiClient.post<ApiResponse<ContactResponse>>(
      `/organizations/${orgId}/contacts`,
      data
    );
    if (!response.data.data.success || !response.data.data.contact) {
      throw apiFailure(response.data.data, 'Failed to create contact');
    }
    return response.data.data.contact;
  },

  updateContact: async (
    orgId: string,
    contactId: string,
    data: UpdateContactRequest
  ): Promise<Contact> => {
    const response = await apiClient.put<ApiResponse<ContactResponse>>(
      `/organizations/${orgId}/contacts/${contactId}`,
      data
    );
    if (!response.data.data.success || !response.data.data.contact) {
      throw apiFailure(response.data.data, 'Failed to update contact');
    }
    return response.data.data.contact;
  },

  deleteContact: async (orgId: string, contactId: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<SimpleVaultResponse>>(
      `/organizations/${orgId}/contacts/${contactId}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to delete contact');
    }
  },

  recordContactUsage: async (orgId: string, chain: string, address: string): Promise<void> => {
    await apiClient.post(`/organizations/${orgId}/contacts/record-usage`, {
      chain,
      address,
    });
  },

  // ============================================================
  // Effective Limits
  // ============================================================

  getEffectiveLimits: async (orgId: string, vaultId: string): Promise<EffectiveLimitsResponse> => {
    const response = await apiClient.get<ApiResponse<EffectiveLimitsResponse>>(
      `/organizations/${orgId}/vaults/${vaultId}/effective-limits`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get effective limits');
    }
    return response.data.data;
  },

  // ============================================================
  // Network Fees (public relay endpoint, no auth required)
  // ============================================================

  fetchNetworkFees: async (): Promise<NetworkFee[]> => {
    const baseUrl = import.meta.env.VITE_API_URL || 'https://relay.sspwallet.com';
    const response = await axios.get<NetworkFee[]>(`${baseUrl}/v1/networkfees`);
    return response.data;
  },

  /** Pending + recently signed/sent proposals across the org's vaults. */
  getOrgProposalActivity: async (
    orgId: string,
    params?: { days?: number }
  ): Promise<OrgProposalActivity> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<{
      data: Partial<OrgProposalActivity> & { success: boolean; error?: string };
    }>(`/organizations/${orgId}/proposal-activity${qs}`);
    const data = response.data.data;
    if (!data.success) {
      throw apiFailure(data, 'Failed to fetch proposal activity');
    }
    return {
      days: data.days ?? params?.days ?? 7,
      since: data.since ?? '',
      pending: data.pending ?? [],
      recentlySigned: data.recentlySigned ?? [],
      recentlySent: data.recentlySent ?? [],
      eligibleSigners: data.eligibleSigners ?? {},
    };
  },

  getSigningRequests: async (orgId: string): Promise<SigningRequest[]> => {
    const response = await apiClient.get<{
      data: {
        success: boolean;
        requests?: SigningRequest[];
        error?: string;
      };
    }>(`/organizations/${orgId}/signing-requests`);
    if (!response.data.data.success || !response.data.data.requests) {
      throw apiFailure(response.data.data, 'Failed to fetch signing requests');
    }
    return response.data.data.requests;
  },
};

export default vaultsApi;
